<template>
  <div class="phase-diagram-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>当前物态 (Phase)</label>
            <span class="val" :style="{color: phaseColor}">{{ currentPhaseName }}</span>
          </div>
          <div class="hud-item orange">
            <label>状态坐标 (P, T)</label>
            <span class="val">({{ focusP.toFixed(0) }}, {{ focusT.toFixed(0) }})</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
            <filter id="glow-phase"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 背景坐标轴 -->
          <g stroke="#334155" stroke-width="1.5">
            <line x1="50" y1="200" x2="380" y2="200" /> <!-- T Axis -->
            <line x1="50" y1="200" x2="50" y2="30" />  <!-- P Axis -->
          </g>
          <text x="360" y="215" fill="#64748b" font-size="10" font-weight="bold">温度 T</text>
          <text x="20" y="40" fill="#64748b" font-size="10" font-weight="bold" transform="rotate(-90, 20, 40)">压强 P</text>

          <!-- 三相区域填充 (示意性) -->
          <!-- 固相 -->
          <path d="M 50,30 L 150,30 Q 140,120 120,200 L 50,200 Z" fill="#10b981" opacity="0.1" />
          <!-- 液相 -->
          <path d="M 150,30 L 350,30 Q 320,80 220,130 Q 180,110 150,30 Z" fill="#38bdf8" opacity="0.1" />
          <!-- 气相 -->
          <path d="M 120,200 Q 140,120 220,130 Q 320,150 380,180 L 380,200 Z" fill="#f59e0b" opacity="0.1" />

          <!-- 相界线 -->
          <!-- 熔化线 (Solid-Liquid) -->
          <path d="M 150,30 Q 145,100 120,200" fill="none" stroke="#10b981" stroke-width="2.5" />
          <!-- 气化线 (Liquid-Gas) -->
          <path d="M 150,30 Q 250,50 350,110" fill="none" stroke="#38bdf8" stroke-width="2.5" />
          <!-- 升华线 (Solid-Gas) -->
          <path d="M 120,200 Q 130,140 150,30" fill="none" stroke="#f59e0b" stroke-width="2.5" />

          <!-- 特殊点 -->
          <g transform="translate(150, 30)">
             <circle r="5" fill="#fff" filter="url(#glow-phase)" />
             <text x="10" y="5" fill="#fff" font-size="10" font-weight="bold">三相点 (Triple Point)</text>
          </g>

          <text x="80" y="100" fill="#10b981" font-size="12" font-weight="bold">固态</text>
          <text x="230" y="90" fill="#38bdf8" font-size="12" font-weight="bold">液态</text>
          <text x="280" y="170" fill="#f59e0b" font-size="12" font-weight="bold">气态</text>

          <!-- 状态指示点 -->
          <g :transform="`translate(${50 + focusT * 3}, ${200 - focusP * 1.6})`">
             <circle r="8" :fill="phaseColor" filter="url(#glow-phase)">
                <animate attributeName="r" values="6;9;6" dur="1s" repeatCount="indefinite" />
             </circle>
             <circle r="3" fill="#fff" />
          </g>
        </svg>

        <div class="phase-molecular-preview">
           <div class="title">微观结构模拟</div>
           <div class="molecular-box" :class="currentPhaseName.toLowerCase()">
              <div v-for="i in 20" :key="i" class="molecule"></div>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center gap-4">
                 <span class="label" style="width: 60px">温度 T</span>
                 <el-slider v-model="focusT" :min="0" :max="100" size="small" class="flex-1"></el-slider>
              </div>
              <div class="flex items-center gap-4">
                 <span class="label" style="width: 60px">压强 P</span>
                 <el-slider v-model="focusP" :min="0" :max="100" size="small" class="flex-1"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>相图的分界线代表两相共存的平衡状态。三条线汇聚形成的<strong>三相点</strong>是该物质固、液、气三相唯一可以稳定共存的特定温压点。观察：增加压强可以使气态转化为液态或固态（取决于温度段）。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PhaseDiagramViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      focusT: 35,
      focusP: 65
    }
  },
  computed: {
    currentPhaseName() {
       // Approximate boundary logic matching SVG paths
       if (this.focusT < 30) {
          // Primarily solid/liquid/gas based on P
          if (this.focusP > 100 - this.focusT * 2) return 'SOLID';
          return 'GAS';
       }
       if (this.focusP > (this.focusT - 30) * 0.8 + 30) return 'LIQUID';
       return 'GAS';
    },
    phaseColor() {
       const map = { 'SOLID': '#10b981', 'LIQUID': '#38bdf8', 'GAS': '#f59e0b' };
       return map[this.currentPhaseName] || '#fff';
    }
  }
}
</script>

<style scoped lang="scss">
.phase-diagram-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f59e0b; }
    }
  }
}

.phase-molecular-preview {
  position: absolute; bottom: 12px; right: 12px; background: rgba(15, 23, 42, 0.8); padding: 8px; border-radius: 8px; border: 1px solid #334155; width: 80px;
  .title { font-size: 9px; color: #94a3b8; text-align: center; margin-bottom: 4px; font-weight: bold; }
  .molecular-box {
    height: 50px; position: relative; border-radius: 4px; background: #0f172a; overflow: hidden;
    .molecule { width: 4px; height: 4px; background: #fff; border-radius: 50%; position: absolute; }
    
    &.solid .molecule { 
       position: static; display: inline-block; margin: 2px;
       animation: jiggle 0.5s infinite alternate;
    }
    &.liquid .molecule { 
       animation: drift 2s infinite linear;
    }
    &.gas .molecule { 
       animation: bounce 1s infinite linear;
    }
  }
}

@keyframes jiggle { from { transform: translate(0,0); } to { transform: translate(1px, 1px); } }
@keyframes drift { from { transform: translate(0,0); } to { transform: translate(10px, 5px); } }
@keyframes bounce { 
  0% { transform: translate(0,0); } 
  25% { transform: translate(30px, 10px); }
  50% { transform: translate(15px, 40px); }
  100% { transform: translate(0,0); }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .flex-1 { flex: 1; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #3b82f6; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
